// pos_local /v1 cavablarını front-un gözlədiyi cloud (Laravel) shape-inə çevirən
// xəritələmələr. Bütün "impedance mismatch" burada toplanır — front komponentləri
// dəyişmir, ona görə sabah cloud frontu yenilənəndə yalnız bu fayla baxmaq kifayətdir.
//
// QEYD (data gaps): pos_local POS-əməliyyat scope-udur. Aşağıdakı sahələr pos_local-da
// YOXDUR və default/boş verilir (hibrid rejimdə bunlar ya admin/cloud-dan gəlir, ya da
// gələcəkdə pos_local genişləndiriləndə doldurulacaq):
//   - table_group_id, qruplar      → local-da qrup yoxdur (hamısı "Tümü")
//   - empty/booked_table_color     → default rənglər
//   - waiter_name, guest_count     → undefined
//   - ps_timer_status, book_time   → reserved statusundan törədilir
//   - total_price                  → 0 (order totalı ayrıca /v1/orders/:id ilə alınır)

export interface PosTable {
  id: string;
  branchId?: string;
  label: string;
  status: 'free' | 'occupied' | 'reserved' | 'dirty';
  currentOrderId?: string | null;
  capacity?: number;
  version?: number;
}

/** Default masa rəngləri (cloud `empty_table_color`/`booked_table_color` qarşılığı). */
export const DEFAULT_TABLE_COLORS = {
  empty: '#22c55e', // boş — yaşıl
  booked: '#ef4444', // dolu/rezerv — qırmızı
};

// ─── Masa bölmələri (table groups) ────────────────────────────────────────────
// pos_local floorplan-da struktur qrup yoxdur, amma bölmə adı label-ın içindədir:
// "Salon - Masa 1" → bölmə "Salon". Buradan front-un gözlədiyi qrupları törədirik.
// (Strukturlu sync gələcəkdə cloud→pos_local→gateway ilə əvəzlənə bilər; o zaman
//  yalnız bu funksiyalar dəyişir, front toxunulmaz.)

const GROUP_SEP = ' - ';

/** Bölmə adından stabil müsbət numerik id (front masaType-i Number()-ləyir). */
function groupIdForSection(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) | 0;
  }
  return Math.abs(h) || 1; // 0 "Tümü" üçün rezerv
}

/** Label-dan bölmə adını çıxar ("Salon - Masa 1" → "Salon"); yoxdursa null. */
function sectionOf(label: string): string | null {
  const idx = label.indexOf(GROUP_SEP);
  if (idx > 0) {
    const s = label.slice(0, idx).trim();
    return s || null;
  }
  return null;
}

export interface DerivedGroups {
  /** front sections: [{id, name}] */
  groups: { id: number; name: string }[];
  /** tableId → groupId (null = bölməsiz) */
  byTableId: Record<string, number | null>;
}

export function deriveTableGroups(tables: PosTable[]): DerivedGroups {
  const nameToId = new Map<string, number>();
  const byTableId: Record<string, number | null> = {};
  for (const t of tables) {
    const section = sectionOf(t.label);
    if (section) {
      const id = groupIdForSection(section);
      if (!nameToId.has(section)) nameToId.set(section, id);
      byTableId[t.id] = id;
    } else {
      byTableId[t.id] = null;
    }
  }
  const groups = Array.from(nameToId.entries()).map(([name, id]) => ({ id, name }));
  return { groups, byTableId };
}

/** GET /table-groups → front `fetchGroups` `[{id, name}]` (label-dan törədilmiş bölmələr). */
export function posTableGroupsToCloud(tables: PosTable[]): { id: number; name: string }[] {
  return deriveTableGroups(tables).groups;
}

/** pos_local masasını → front-un gözlədiyi cloud masa obyektinə çevirir. */
export function posTableToCloud(
  t: PosTable,
  groupId: number | null = null,
  totalCents = 0,
): Record<string, unknown> {
  // Açıq sifariş varsa masa "dolu" sayılır (status ya currentOrderId).
  const occupied = t.status === 'occupied' || t.status === 'dirty' || !!t.currentOrderId;
  const reserved = t.status === 'reserved';
  return {
    id: t.id,
    name: t.label,
    // cloud konvensiyası: is_available truthy = BOŞ
    is_available: occupied ? 0 : 1,
    book_time: reserved ? new Date().toISOString() : null,
    table_group_id: groupId,
    guest_count: undefined,
    total_price: totalCents / 100,
    waiter_name: undefined,
    user_name: undefined,
    ps_timer_status: null,
    expiredMinutes: 0,
    orders: t.currentOrderId ? [{ order_id: t.currentOrderId }] : [],
    order_items: [],
    capacity: t.capacity ?? null,
    currentOrderId: t.currentOrderId ?? null,
    _posVersion: t.version,
  };
}

/**
 * GET /v1/tables → front `fetchTables` `{ tables, empty_table_color, booked_table_color }`.
 * filterGroupId verilibsə yalnız o bölmənin masalarını qaytarır (section tab).
 * totalsByTableId: açıq sifarişi olan masaların cəmi (cents) — masa grid-də tutar.
 */
export function posTablesListToCloud(
  tables: PosTable[],
  filterGroupId?: number | null,
  totalsByTableId?: Record<string, number>,
): Record<string, unknown> {
  const { byTableId } = deriveTableGroups(tables);
  const selected =
    filterGroupId == null
      ? tables
      : tables.filter((t) => byTableId[t.id] === filterGroupId);
  return {
    tables: selected.map((t) =>
      posTableToCloud(t, byTableId[t.id] ?? null, totalsByTableId?.[t.id] ?? 0),
    ),
    empty_table_color: DEFAULT_TABLE_COLORS.empty,
    booked_table_color: DEFAULT_TABLE_COLORS.booked,
  };
}
